<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'DataViewerModal',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    headerText: {
      type: String,
      required: false,
    },
    content: {
      type: Object,
      required: true,
    },
    mode: {
      type: String,
      required: false,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    noHeader: false,
    noPrimaryButton: true,
    size: 'lg',
  }));
  const b_8564662037462133_options = computed(() => ({
    lineNumbers: true,
    matchBrackets: true,
    continueComments: 'Enter',
  }));

  const $data = computed(() => ({
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_8564662037462133_options: b_8564662037462133_options.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_8564662037462133_options', nodeId: '8564662037462133' },
    ],
    loaders: {},
    ux: {},
    dataRefs: {
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_8564662037462133_options,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const inputData = computed(() => {
    return props.mode === 'json'
      ? _.isString(props.content)
        ? props.content
        : JSON.stringify(props.content, null, 2)
      : props.content;
  });
  const pageDocument = computed(() => {
    return { title: props.headerText || $fn.value.gettext('View Data') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.headerText,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('headerText');
    }
  );
  watch(
    () => props.content,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('content');
    }
  );
  watch(
    () => props.mode,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('mode');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_4e9f9qm2q_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };

  const act_7315092382398562_closeModal_4e9f9qm2q =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_4e9f9qm2q_getActionArgs,
    });
  const inst_evh_7315092382398562_close_sb0pa7o10 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_4e9f9qm2q,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_sb0pa7o10],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_x7ftpgcpn =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_jxajk4eg9 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_x7ftpgcpn,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_jxajk4eg9],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_close_sb0pa7o10,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_jxajk4eg9,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_close_sb0pa7o10 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_sb0pa7o10.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_jxajk4eg9 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_jxajk4eg9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="DataViewerModal"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="container-fluid">
      <ItpCodeMirror
        :mode="mode"
        :options="b_8564662037462133_options"
        v-model="inputData"
        :value="inputData"
        @input="
          !($event && typeof $event.stopPropagation === 'function') &&
          (inputData =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event)
        "
      >
      </ItpCodeMirror>
    </div>
  </ItpPage>
</template>
